import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { treatExperiment } from '#src/app/shared/api/experiments';
import { AMBASSADOR_SOURCE_LOCATION } from '#src/app/shared/constants';
import type { StartStrongTreatOutcome } from '#src/app/shared/utils/startStrong';
import { normalize } from '#src/app/shared/utils/string';

import type { PublishedPetitionResponse } from './api/createPublishedPetition';
import { isViewerFirstPublishedPetition } from './api/getViewerStartedPetitionsCount';
import { fetchSocialStarterAnglePublishData } from './api/socialStarterAngleSourceLocation';
import { withTimeout } from './shared/withTimeout';

// The caller awaits this before resolving the post-publish redirect, so a hung treat call must
// reject (and fail closed to untreated) rather than block publish indefinitely — same rationale
// and value as claimSocialStarterPetitionAngles and isViewerFirstPublishedPetition.
const TREAT_EXPERIMENT_TIMEOUT_MS = 8000;

/**
 * Whether the resolved `source_location` identifies an ambassador petition, independent of the
 * `ambassadorPostCreateFlow` feature config. Used to keep ambassador petitions out of the
 * starter_experience_start_strong_after_sap experiment entirely, regardless of whether the
 * ambassador-onboarding redirect itself is currently enabled — an ambassador petition shouldn't be
 * eligible for a *different* post-publish experience just because its own dedicated flow is off.
 *
 * Branches on the resolved `source_location` rather than the petition's `isAmbassadorPetition`
 * field: staffing is applied asynchronously, so that field can still read false here — and that
 * false would then be cached for 30 minutes.
 */
export function isAmbassadorSourceLocation({
	resolvedSourceLocation,
}: {
	resolvedSourceLocation: string | undefined;
}): boolean {
	return resolvedSourceLocation === AMBASSADOR_SOURCE_LOCATION;
}

/**
 * Whether a publish redirects to ambassador-onboarding — gated on `isAmbassadorPostCreateFlowEnabled`
 * so flipping that config off restores the pre-experiment behavior exactly (an ambassador petition
 * simply falls back to the standard onboarding flow, same as before this feature existed). Only
 * used by `resolvePostPublishPath` below — the exposure-eligibility check in `usePrePublish/index.ts`
 * deliberately uses the flag-independent `isAmbassadorSourceLocation` instead (see there for why).
 */
function isAmbassadorPetitionRedirect({
	resolvedSourceLocation,
	isAmbassadorPostCreateFlowEnabled,
}: {
	resolvedSourceLocation: string | undefined;
	isAmbassadorPostCreateFlowEnabled: boolean;
}): boolean {
	return isAmbassadorPostCreateFlowEnabled && isAmbassadorSourceLocation({ resolvedSourceLocation });
}

/**
 * Checks whether an AI draft has been edited by comparing the normalized base and current values.
 * This information is passed in the activate_petition tracking event.
 */
export function compareNormalizedStrings(base: string | undefined | null, value: string): boolean | null {
	if (base === undefined || base === null) return null;

	return normalize(base) !== normalize(value);
}

/**
 * Resolves the Social Starter data to publish with. The draft-angle record is the source of truth
 * for both staffing attribution and the matched decision maker. Non-Social-Starter drafts preserve
 * the URL attribution and have no decision maker.
 */
export async function resolvePublishContext({
	angleToken,
	urlSourceLocation,
	utilityContext,
}: {
	angleToken: string | null | undefined;
	urlSourceLocation: string | undefined;
	utilityContext: UtilityContext;
}): Promise<Readonly<{ sourceLocation: string | undefined; decisionMakerId: string | undefined }>> {
	if (!angleToken) return { sourceLocation: urlSourceLocation, decisionMakerId: undefined };

	const publishData = await fetchSocialStarterAnglePublishData({ token: angleToken, utilityContext });
	// A blank stamped code shouldn't happen, but guard it the same as a missing one — the petition
	// service validates source_location against a fixed set, so an empty string would 400 rather
	// than silently falling back to the URL value.
	return {
		sourceLocation: publishData?.sourceLocation || urlSourceLocation,
		decisionMakerId: publishData?.decisionMakerId ?? undefined,
	};
}

/**
 * Resolves whether the viewer is (or becomes) treated in starter_experience_start_strong_after_sap,
 * for use by `resolvePostPublishPath` — so start-strong is never shown to someone who isn't
 * actually registered as treated, which would corrupt the experiment's data the same way an
 * untreated-but-visible variant would for any other experiment in this codebase.
 *
 * `alreadyTreated` covers a viewer already treated on an earlier petition. Otherwise, treat now —
 * but only if this is the viewer's first published petition: petition.v1.petition_published (and
 * the publish mutation) fire on every republish, and a user can start many petitions, so treating
 * on a later one would attribute the exposure to the wrong petition. See the equivalent
 * first-petition gating on the backend.
 *
 * A QA/dev `override` is a forced assignment, not a real one, so it skips the treat attempt
 * entirely — same as every other overridable experiment in this codebase — but still counts as
 * enabled so overrides can exercise the variant. `treatExperiment`'s own return value is used (not
 * assumed `true`) since a bot session or not-yet-treatable assignment can resolve without error
 * while treated stays false server-side.
 *
 * `fallback` (the FCM is disabled, or experiment resolution failed) is, unlike `override`, never a
 * real assignment worth exercising — it's an error/default state, not a deliberate choice — so it
 * disables both the treat attempt and the redirect outright, before any other check runs.
 *
 * Both non-control arms (`variant_a` and `variant_b`) get the redirect — only `control` stays on
 * the standard onboarding flow. The two variants currently differ elsewhere (e.g. dashboard task
 * set), not in whether they see start-strong after publish.
 *
 * An ambassador petition never renders start-strong regardless of variation (see
 * `resolvePostPublishPath`), so it must never attempt or record a start-strong exposure either.
 *
 * A treat timeout is reported separately from other treat failures via `trackTreatExperimentTimeout`:
 * the underlying request isn't cancelled on timeout, so it can still land and treat the viewer
 * server-side even though this resolves `false` here — a real (if rare) mismatch between what the
 * client showed and what the experiment recorded that's worth letting analysts filter out, unlike an
 * outright request failure where nothing was recorded either side.
 *
 * Also returns why, as `treatOutcome`, and how long the whole resolution took (first-petition check
 * plus treat), as `resolveLatencyMs`, for the `StartStrongRedirectResolved` New Relic event. There
 * is no `error` outcome: `treatExperiment` catches its own errors and resolves `false`, so the only
 * rejection the treat's rejection handler sees is the timeout.
 */
export async function resolveIsStartStrongAfterSapEnabled({
	experimentName,
	variation,
	alreadyTreated,
	override,
	fallback,
	isAmbassadorPetition,
	petitionId,
	utilityContext,
	trackTreatExperimentTimeout,
}: {
	experimentName: string;
	variation: string;
	alreadyTreated: boolean;
	override: true | undefined;
	fallback: true | undefined;
	isAmbassadorPetition: boolean;
	petitionId: string;
	utilityContext: UtilityContext;
	trackTreatExperimentTimeout: (args: { experimentName: string; variation: string; petitionId: string }) => void;
}): Promise<StartStrongAfterSapResolution> {
	const startedAt = performance.now();
	const resolved = (enabled: boolean, treatOutcome: StartStrongTreatOutcome): StartStrongAfterSapResolution => ({
		enabled,
		treatOutcome,
		resolveLatencyMs: Math.round(performance.now() - startedAt),
	});

	if (isAmbassadorPetition || fallback === true) return resolved(false, 'ineligible');
	if (override === true) return resolved(variation !== 'control', 'override');

	if (!alreadyTreated) {
		if (!(await isViewerFirstPublishedPetition(utilityContext))) return resolved(false, 'not_first_petition');

		// Timed out and caught (not just left to `treatExperiment`'s own internal catch): a hung
		// underlying request would otherwise never resolve, since `treatExperiment` only catches a
		// rejection from its own fetch, not a request that simply never settles. Falls back to
		// untreated on timeout, same fail-closed behavior as `isViewerFirstPublishedPetition`.
		const treatResult: 'treated' | 'not_treated' | 'timeout' = await withTimeout(
			treatExperiment(utilityContext, experimentName),
			TREAT_EXPERIMENT_TIMEOUT_MS,
			`Treat ${experimentName} timed out after ${TREAT_EXPERIMENT_TIMEOUT_MS}ms`,
		).then(
			(treated) => (treated ? 'treated' : 'not_treated'),
			(error: unknown) => {
				void utilityContext.errorReporter.report({
					error: error instanceof Error ? error : new Error(`Failed to treat ${experimentName}`, { cause: error }),
				});
				if (error instanceof Error && /timed out/i.test(error.message)) {
					trackTreatExperimentTimeout({ experimentName, variation, petitionId });
					return 'timeout';
				}
				// Unreachable today (see above); mapped so no rejection goes unrecorded.
				return 'not_treated';
			},
		);

		// `not_treated` is a bot session, an assignment that can't be treated yet, or a GQL failure
		// `treatExperiment` swallowed — an eligible, bucketed starter who silently gets the old onboarding.
		if (treatResult !== 'treated') return resolved(false, treatResult);
	}

	return variation === 'control' ? resolved(false, 'control') : resolved(true, 'treated');
}

type StartStrongAfterSapResolution = Readonly<{
	enabled: boolean;
	treatOutcome: StartStrongTreatOutcome;
	resolveLatencyMs: number;
}>;

type PostPublishDestination = 'ambassador' | 'start_strong' | 'starter_onboarding';

/**
 * Where a publish redirects to — the `destination` on `StartStrongRedirectResolved`, and the branch
 * `resolvePostPublishPath` builds its path from, so the two can't disagree.
 *
 * The ambassador check takes precedence over the start-strong redirect: an ambassador petition
 * always goes to ambassador-onboarding regardless of `isStartStrongAfterSapEnabled`.
 *
 * `isStartStrongAfterSapEnabled` must already reflect whether the viewer is actually treated (or
 * overridden) in the experiment — see the caller's exposure resolution in `usePrePublish/index.ts`.
 * This function only applies the ambassador/redirect precedence; it doesn't re-derive eligibility.
 */
export function resolvePostPublishDestination({
	resolvedSourceLocation,
	isAmbassadorPostCreateFlowEnabled,
	isStartStrongAfterSapEnabled,
}: {
	resolvedSourceLocation: string | undefined;
	isAmbassadorPostCreateFlowEnabled: boolean;
	isStartStrongAfterSapEnabled: boolean;
}): PostPublishDestination {
	if (isAmbassadorPetitionRedirect({ resolvedSourceLocation, isAmbassadorPostCreateFlowEnabled })) return 'ambassador';
	if (isStartStrongAfterSapEnabled) return 'start_strong';
	return 'starter_onboarding';
}

const POST_PUBLISH_PATH_SUFFIXES: Readonly<Record<PostPublishDestination, string>> = {
	ambassador: 'ambassador-onboarding',
	start_strong: 'start-strong',
	starter_onboarding: 'starter-onboarding/welcome',
};

export function resolvePostPublishPath({
	petition,
	destination,
}: {
	petition: Pick<PublishedPetitionResponse, 'id' | 'slug'>;
	destination: PostPublishDestination;
}): string {
	return `/p/${petition.slug ?? petition.id}/${POST_PUBLISH_PATH_SUFFIXES[destination]}`;
}
